import { Map } from 'immutable';
import { LOCATION_CHANGE } from 'react-router-redux';

import { ApplicationStateKeys } from '../constants/Application';
import * as D from '../deps';

const INITIAL_STATE = Map({
  [ApplicationStateKeys.APPLICATION_INITIALIZED]: false,
  [ApplicationStateKeys.SERVICE_ERRORS]: 0,
  [ApplicationStateKeys.IS_BUFFERING_API_CALLS]: false,
  [ApplicationStateKeys.IS_404_PAGE]: false,
  [ApplicationStateKeys.COACHMARK_PATH]: '',
  [ApplicationStateKeys.IS_OFFLINE]: false,
  [ApplicationStateKeys.LAST_OFFLINE_ACTION]: null,
  [ApplicationStateKeys.IS_PERSISTING_LOCALLY]: true,
  [ApplicationStateKeys.STARTUP_QUERY_PARAMETERS]: Map(),
  [ApplicationStateKeys.IS_LOGGING_OUT]: false,
  [ApplicationStateKeys.IS_PLAYLIST_PAGE]: false,
  [ApplicationStateKeys.IS_SEARCH_OPEN]: false,
  [ApplicationStateKeys.CURRENT_PATH_FOR_COUNTING_URL_CHANGES]: document.location.pathname,
  [ApplicationStateKeys.PREVIOUS_PATH]: '',
  [ApplicationStateKeys.ROUTED_COUNT]: 0,
  [ApplicationStateKeys.IS_AD_BLOCKER_DETECTED]: false,
  [ApplicationStateKeys.IS_ANTI_AD_BLOCKER_HAMMER_MODE_DETECTED]: false,
  [ApplicationStateKeys.IS_BROWSER_AUTOPLAY_BLOCK_DETECTED]: false,
  [ApplicationStateKeys.IS_BROWSER_AUTOPLAY_BLOCK_OPPORTUNITY_HAPPENED]: false,
});

// APPLICATION REDUCER
export default D.createReducer(INITIAL_STATE, {
  [D.AppConstants.AppActions.APPLICATION_INITIALIZE]: applicationInitializeComplete,
  [D.ApiActions.LOGOUT_ATTEMPT]: logoutAttempt,
  [D.ApiActions.LOGOUT_SUCCESS]: logoutSuccess,
  [D.ApiActions.LOGOUT_FAIL]: logoutFail,
  [D.AppConstants.AppActions.TOGGLE_OFFLINE_MODE]: toggleOfflineMode,
  [D.AppConstants.AppActions.SET_LAST_OFFLINE_ACTION]: setLastOfflineAction,
  [D.AppConstants.AppActions.SERVICE_ERROR]: serviceError,
  [D.AppConstants.AppActions.SERVICE_OK]: serviceOk,
  [D.AppConstants.AppActions.BUFFER_API_CALLS]: bufferApiCalls,
  [D.AppConstants.AppActions.ENTER_404]: enter404,
  [D.AppConstants.AppActions.LEAVE_404]: leave404,
  [D.AppConstants.AppActions.SHOW_COACHMARK]: showCoachmark,
  [D.AppConstants.AppActions.SET_PERSISTING_STATE]: setPersistingState,
  [D.AppConstants.AppActions.SET_STARTUP_QUERY_PARAMETERS]: setStartupQueryParameters,
  [D.AppConstants.AppActions.SET_IS_AD_BLOCKER_DETECTED]: setIsAdBlockerDetected,
  [D.AppConstants.AppActions.SET_IS_ANTI_AD_BLOCKER_HAMMER_MODE_ENABLED]:
    setIsAntiAdBlockerHammerModeEnabled,
  [D.AppConstants.AppActions.SET_IS_BROWSER_AUTOPLAY_BLOCK_DETECTED]:
    setIsBrowserAutoplayBlockDetected,
  [D.AppConstants.AppActions.SET_BROWSER_AUTOPLAY_BLOCK_OPPORTUNITY_HAPPENED]:
    setBrowserAutoplayBlockOpportunityHappened,
  [D.AppConstants.AppActions.ENTER_PLAYLIST]: enterPlaylist,
  [D.AppConstants.AppActions.LEAVE_PLAYLIST]: leavePlaylist,
  [D.AppConstants.AppActions.ENTER_SEARCH]: enterSearch,
  [D.AppConstants.AppActions.LEAVE_SEARCH]: leaveSearch,
  [LOCATION_CHANGE]: locationChange,
});

// CASE FUNCTIONS

function applicationInitializeComplete(state) {
  /* set flag that initialization has completed and flag of mobile web client */
  return state.set(ApplicationStateKeys.APPLICATION_INITIALIZED, true);
}

function logoutAttempt(state) {
  return state.set(ApplicationStateKeys.IS_LOGGING_OUT, true);
}

function logoutSuccess() {
  return INITIAL_STATE.set(ApplicationStateKeys.IS_LOGGING_OUT, true);
}

function logoutFail(state) {
  return state.set(ApplicationStateKeys.IS_LOGGING_OUT, false);
}

function toggleOfflineMode(state, { payload }) {
  return state.set(ApplicationStateKeys.IS_OFFLINE, payload.isOffline);
}

function setIsAdBlockerDetected(state, { payload }) {
  return state.set(ApplicationStateKeys.IS_AD_BLOCKER_DETECTED, payload.loaded);
}

function setIsAntiAdBlockerHammerModeEnabled(state, { payload }) {
  return state.set(ApplicationStateKeys.IS_ANTI_AD_BLOCKER_HAMMER_MODE_DETECTED, payload.isEnabled);
}

function setIsBrowserAutoplayBlockDetected(state, { payload }) {
  return state.set(ApplicationStateKeys.IS_BROWSER_AUTOPLAY_BLOCK_DETECTED, payload.detected);
}

function setBrowserAutoplayBlockOpportunityHappened(state) {
  return state.set(ApplicationStateKeys.IS_BROWSER_AUTOPLAY_BLOCK_OPPORTUNITY_HAPPENED, true);
}

function setLastOfflineAction(state, { payload }) {
  return state.set(ApplicationStateKeys.LAST_OFFLINE_ACTION, payload.lastOfflineAction);
}

function serviceError(state) {
  return state.set(
    ApplicationStateKeys.SERVICE_ERRORS,
    state.get(ApplicationStateKeys.SERVICE_ERRORS) + 1,
  );
}

function serviceOk(state) {
  return state.set(ApplicationStateKeys.SERVICE_ERRORS, 0);
}

function bufferApiCalls(state, { payload }) {
  return state.set(ApplicationStateKeys.IS_BUFFERING_API_CALLS, !!payload.buffer);
}

function enter404(state) {
  return state.set(ApplicationStateKeys.IS_404_PAGE, true);
}

function leave404(state) {
  return state.set(ApplicationStateKeys.IS_404_PAGE, false);
}

function showCoachmark(state, { payload }) {
  return state.set(ApplicationStateKeys.COACHMARK_PATH, payload.showCoachmark);
}

function setPersistingState(state, { payload }) {
  return state.set(ApplicationStateKeys.IS_PERSISTING_LOCALLY, payload.isPersisting);
}

function setStartupQueryParameters(state, { payload }) {
  return state.set(
    ApplicationStateKeys.STARTUP_QUERY_PARAMETERS,
    Map(payload.startupQueryParameters),
  );
}
function enterPlaylist(state) {
  return state.set(ApplicationStateKeys.IS_PLAYLIST_PAGE, true);
}

function leavePlaylist(state) {
  return state.set(ApplicationStateKeys.IS_PLAYLIST_PAGE, false);
}

function enterSearch(state) {
  return state.set(ApplicationStateKeys.IS_SEARCH_OPEN, true);
}

function leaveSearch(state) {
  return state.set(ApplicationStateKeys.IS_SEARCH_OPEN, false);
}

function locationChange(state, { payload: { pathname } }) {
  const currentPath = state.get(ApplicationStateKeys.CURRENT_PATH_FOR_COUNTING_URL_CHANGES);
  const previousPath =
    pathname === currentPath ? state.get(ApplicationStateKeys.PREVIOUS_PATH) : currentPath;
  const currentRoutedCount = state.get(ApplicationStateKeys.ROUTED_COUNT);
  const routedCount = pathname === currentPath ? currentRoutedCount : currentRoutedCount + 1;

  return state.merge({
    [ApplicationStateKeys.CURRENT_PATH_FOR_COUNTING_URL_CHANGES]: pathname,
    [ApplicationStateKeys.PREVIOUS_PATH]: previousPath,
    [ApplicationStateKeys.ROUTED_COUNT]: routedCount,
  });
}
